/**
 * Login page — email OTP, passkey, and Google sign-in.
 *
 * Route: /auth/login
 * States: idle → loading → otp-challenge → error
 */

import { LitElement, html, css, nothing } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { authService } from '../../services/auth/auth-service.js'
import { authConfig } from '../../services/auth/auth-config.js'
import { CognitoError } from '../../services/auth/cognito-api.js'

import '@material/web/button/filled-button.js'
import '@material/web/button/outlined-button.js'
import '@material/web/button/text-button.js'
import '@material/web/textfield/outlined-text-field.js'
import '@material/web/icon/icon.js'
import '@material/web/progress/circular-progress.js'

import './auth-otp-verify.js'

type LoginState = 'idle' | 'loading' | 'otp-challenge' | 'error'

@customElement('auth-login')
export class AuthLogin extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;
      padding: var(--cairn-spacing-xl, 32px) var(--cairn-spacing-md, 16px);
      box-sizing: border-box;
    }

    .container {
      max-width: 400px;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .logo {
      width: 96px;
      height: 96px;
      margin-bottom: var(--cairn-spacing-sm, 8px);
    }

    .logo img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }

    h2 {
      font-size: var(--cairn-text-size-xl, 24px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-color, #1e293b);
      margin: 0 0 var(--cairn-spacing-xs, 4px);
    }

    .subtitle {
      font-size: var(--cairn-text-size-md, 16px);
      color: var(--cairn-text-secondary-color, #64748b);
      margin: 0 0 var(--cairn-spacing-lg, 24px);
    }

    md-outlined-text-field {
      width: 100%;
      margin-bottom: var(--cairn-spacing-md, 16px);
      --md-outlined-text-field-container-shape: var(--cairn-border-radius, 8px);
    }

    .actions {
      display: flex;
      flex-direction: column;
      gap: var(--cairn-spacing-sm, 8px);
      width: 100%;
      margin-bottom: var(--cairn-spacing-md, 16px);
    }

    .actions-row {
      display: flex;
      flex-direction: row;
      gap: var(--cairn-spacing-sm, 8px);
      width: 100%;
      margin-bottom: var(--cairn-spacing-md, 16px);
    }

    .actions-row md-filled-button,
    .actions-row md-outlined-button {
      flex: 1;
    }

    md-filled-button,
    md-outlined-button {
      width: 100%;
      --md-filled-button-container-height: 48px;
      --md-filled-button-container-shape: var(--cairn-border-radius, 8px);
      --md-outlined-button-container-height: 48px;
      --md-outlined-button-container-shape: var(--cairn-border-radius, 8px);
    }

    .google-icon {
      width: 18px;
      height: 18px;
    }

    .passkey-icon {
      font-variation-settings: 'wght' 600;
    }

    .divider {
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-md, 16px);
      width: 100%;
      margin: var(--cairn-spacing-md, 16px) 0;
      color: var(--cairn-text-muted-color, #94a3b8);
      font-size: var(--cairn-text-size-sm, 14px);
    }

    .divider::before,
    .divider::after {
      content: '';
      flex: 1;
      border-top: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .error-message {
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-error-color, #dc2626);
      margin: 0 0 var(--cairn-spacing-md, 16px);
      text-align: center;
    }

    .footer {
      margin-top: var(--cairn-spacing-lg, 24px);
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #64748b);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .footer md-text-button {
      --md-text-button-label-text-weight: 600;
    }

    .loading-overlay {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--cairn-spacing-md, 16px);
      padding: var(--cairn-spacing-xl, 32px);
      color: var(--cairn-text-secondary-color, #64748b);
    }
  `

  @state() private loginState: LoginState = 'idle'
  @state() private email = ''
  @state() private error = ''
  @state() private otpSession = ''

  override connectedCallback(): void {
    super.connectedCallback()
    this.email = authService.getLastEmail() ?? ''
  }

  private async handleEmailSubmit(e: Event): Promise<void> {
    e.preventDefault()
    const email = this.email.trim().toLowerCase()
    if (!email) return

    this.loginState = 'loading'
    this.error = ''

    try {
      const result = await authService.loginWithEmailOtp(email)
      if (result.challengeName === 'EMAIL_OTP' && result.session) {
        this.otpSession = result.session
        this.loginState = 'otp-challenge'
      } else {
        this.error = 'Unexpected response. Please try again.'
        this.loginState = 'error'
      }
    } catch (err) {
      this.loginState = 'error'
      this.error = this.formatError(err)
    }
  }

  private async handleOtpSubmitted(e: CustomEvent<{ code: string }>): Promise<void> {
    this.error = ''
    try {
      await authService.verifyOtp(this.otpSession, this.email.trim().toLowerCase(), e.detail.code)
      await this.navigateAfterLogin()
    } catch (err) {
      this.error = this.formatError(err)
    }
  }

  private async handleOtpResend(): Promise<void> {
    try {
      const result = await authService.loginWithEmailOtp(this.email.trim().toLowerCase())
      if (result.session) {
        this.otpSession = result.session
      }
      this.error = ''
    } catch (err) {
      this.error = this.formatError(err)
    }
  }

  private async handlePasskeyLogin(): Promise<void> {
    const email = this.email.trim().toLowerCase()
    if (!email) return

    this.loginState = 'loading'
    this.error = ''

    try {
      await authService.loginWithPasskey(email)
      await this.navigateAfterLogin()
    } catch (err) {
      this.loginState = 'error'
      this.error = this.formatError(err)
    }
  }

  private handleGoogleLogin(): void {
    void authService.loginWithGoogle()
  }

  private async navigateAfterLogin(): Promise<void> {
    const { router } = await import('../../utils/router.js')
    if (authService.needsSetup()) {
      router.navigate('/auth/setup')
    } else if (authService.needsEula()) {
      router.navigate('/auth/eula')
    } else {
      router.navigate('/')
    }
  }

  private handleEmailInput(e: Event): void {
    this.email = (e.target as HTMLInputElement).value
    if (this.loginState === 'error') {
      this.loginState = 'idle'
      this.error = ''
    }
  }

  private handleEmailKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter') {
      void this.handleEmailSubmit(e)
    }
  }

  private navigateToRegister(): void {
    void import('../../utils/router.js').then(({ router }) => {
      router.navigate('/auth/register')
    })
  }

  private formatError(err: unknown): string {
    if (err instanceof CognitoError) {
      switch (err.code) {
        case 'NotAuthorizedException':
        case 'UserNotFoundException':
        case 'PasswordResetRequiredException':
          return 'Sign in failed. Check your email or create an account.'
        case 'CodeMismatchException':
          return 'Incorrect code. Please try again.'
        case 'ExpiredCodeException':
          return 'Code expired. We sent a new one.'
        case 'LimitExceededException':
          return 'Too many attempts. Please wait a moment.'
        default:
          return err.message
      }
    }
    if (err instanceof Error) {
      if (err.name === 'NotAllowedError') {
        return 'Passkey login was cancelled.'
      }
      return 'Something went wrong. Please try again.'
    }
    return 'Something went wrong. Please try again.'
  }

  override render(): unknown {
    if (!authConfig.isConfigured) {
      return html`<div class="container">
        <p class="error-message">Authentication is not configured.</p>
      </div>`
    }

    return html`
      <div class="container">
        <div class="logo">
          <img src="/images/cairn-icon-clean.png" alt="Taselith" />
        </div>
        <h2>Welcome back</h2>
        <p class="subtitle">Sign in to your account</p>

        ${this.loginState === 'loading' ? this.renderLoading() : nothing}
        ${this.loginState === 'otp-challenge' ? this.renderOtpChallenge() : nothing}
        ${this.loginState === 'idle' || this.loginState === 'error'
          ? this.renderEmailForm()
          : nothing}
      </div>
    `
  }

  private renderLoading(): unknown {
    return html`
      <div class="loading-overlay">
        <md-circular-progress indeterminate></md-circular-progress>
        <span>Signing in...</span>
      </div>
    `
  }

  private renderOtpChallenge(): unknown {
    return html`
      <auth-otp-verify
        .email=${this.email.trim()}
        .error=${this.error}
        @otp-submitted=${this.handleOtpSubmitted}
        @otp-resend=${this.handleOtpResend}
      ></auth-otp-verify>
    `
  }

  private renderEmailForm(): unknown {
    return html`
      ${this.error ? html`<p class="error-message">${this.error}</p>` : nothing}

      <md-outlined-text-field
        label="Email"
        type="email"
        .value=${this.email}
        @input=${this.handleEmailInput}
        @keydown=${this.handleEmailKeydown}
      ></md-outlined-text-field>

      <div class="${this.email.trim() ? 'actions-row' : 'actions'}">
        <md-filled-button @click=${this.handleEmailSubmit}>
          <md-icon slot="icon">mail</md-icon>
          Email
        </md-filled-button>

        ${this.email.trim()
          ? html`
              <md-outlined-button @click=${this.handlePasskeyLogin}>
                <md-icon slot="icon" class="passkey-icon">fingerprint</md-icon>
                Passkey
              </md-outlined-button>
            `
          : nothing}
      </div>

      <div class="divider">or</div>

      <div class="actions">
        <md-outlined-button @click=${this.handleGoogleLogin}>
          <svg slot="icon" class="google-icon" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Continue with Google
        </md-outlined-button>
      </div>

      <div class="footer">
        Don't have an account?
        <md-text-button @click=${this.navigateToRegister}>Create account</md-text-button>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'auth-login': AuthLogin
  }
}
